/**
 * Why someone is at the door: to have a list of their own, or to pay for someone else's.
 *
 * The two have their own addresses — `/onboarding` and `/onboarding/presentear` — so the
 * intent is read off the route instead of guessed from where the person was going. Everything
 * that leads to the checkout is a purchase, including the host of list A paying for list B;
 * everything that leads to the panel is someone who wants a list.
 *
 * It decides one thing on the way in (what the screens say) and one on the way out: whether a
 * draft gift list is provisioned. A list is provisioned for a host, because a host is here for
 * a list; nothing is provisioned for a guest, who came to send a gift and would otherwise leave
 * with an empty list in their name and terms to accept for it.
 */
export type OnboardingIntent = "host" | "guest";

/** The door the proxy sends someone to when a route asks for a session first. */
export const ONBOARDING_ENTRY: Record<OnboardingIntent, string> = {
  host: "/onboarding",
  guest: "/onboarding/presentear",
};

/**
 * Where a host whose list still owes its terms goes: the creation, which is where the terms are
 * now asked — after the event (§5.6), so the person agrees to the terms of the thing they have
 * just described. Until 2026-10-08 the wizard asked them itself and this said "after terms";
 * the address is the same, what changed is which side of it the acceptance happens on.
 *
 * A `?next=` already pointing into the creation — a chosen house suggestion rides on it — is
 * kept as it is, so the suggestion is not lost on the way through the door.
 */
export function listCreationDestination(redirectTo: string): string {
  const CREATE_LIST = "/anfitriao/nova-lista";

  return redirectTo === CREATE_LIST || redirectTo.startsWith(`${CREATE_LIST}?`) || redirectTo.startsWith(`${CREATE_LIST}/`)
    ? redirectTo
    : CREATE_LIST;
}

/** Where each door leads once the session exists and no `?next=` says otherwise. */
export const ONBOARDING_DESTINATION: Record<OnboardingIntent, string> = {
  host: "/anfitriao/dashboard",
  guest: "/checkout",
};
